/**
 * The strip of applied indicators above the chart, with inline settings.
 *
 * Editing a parameter re-runs the whole batch server-side, which is a single
 * Pine pass, so it stays responsive even with a dozen indicators applied.
 */
import { EVENT_COLOR, onColor } from "@tt/shared";
import type { IndicatorDef, IndicatorInstance } from "@tt/shared";
import { useEffect, useRef, useState } from "react";

import { IndicatorSettings } from "./IndicatorSettings";
import { ToolIcon } from "./ToolIcon";

export function AppliedIndicators({
  instances, byId, onRemove, onUpdate, onToggleVisible, onAdd, onClear, busy,
  volumeMode, onVolumeMode, extendedHours, onExtendedHours, intraday, volumeNote,
  eventKinds, onEventKinds, eventCounts,
}: {
  instances: IndicatorInstance[];
  byId: Map<string, IndicatorDef>;
  onRemove: (instanceId: string) => void;
  onUpdate: (instanceId: string, params: Record<string, unknown>) => void;
  onToggleVisible: (instanceId: string) => void;
  volumeMode: "off" | "overlay" | "pane";
  onVolumeMode: (m: "off" | "overlay" | "pane") => void;
  extendedHours: boolean;
  onExtendedHours: (v: boolean) => void;
  /** Extended hours only exist below the daily timeframe. */
  intraday: boolean;
  volumeNote: string | null;
  eventKinds: Record<string, boolean>;
  onEventKinds: (v: Record<string, boolean>) => void;
  eventCounts: Record<string, number>;
  onAdd: () => void;
  onClear: () => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setEditing(null);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  return (
    <div className="applied" ref={boxRef}>
      <button className="btn small primary" onClick={onAdd}>+ Indicators</button>

      <div className={`chip ${volumeMode === "off" ? "off" : ""}`}>
        <button
          className="chip-eye"
          title={volumeMode === "off" ? "Show volume" : "Hide volume"}
          onClick={() => onVolumeMode(volumeMode === "off" ? "overlay" : "off")}
        >
          <ToolIcon name={volumeMode === "off" ? "eye-off" : "eye"} size={11} />
        </button>
        <span>Volume</span>
        <select
          className="select"
          style={{ padding: "0 2px", fontSize: 11.5, width: 72 }}
          value={volumeMode}
          onChange={(e) => onVolumeMode(e.target.value as "off" | "overlay" | "pane")}
          title="Where volume is drawn"
        >
          <option value="overlay">on price</option>
          <option value="pane">own pane</option>
          <option value="off">off</option>
        </select>
      </div>

      <div className="chip">
        <span>Events</span>
        <span className="event-toggles">
          {([
            ["earnings", "E", "Earnings"],
            ["dividend", "D", "Dividends"],
            ["split", "S", "Splits"],
            ["news", "N", "Material news"],
          ] as const).map(([kind, glyph, label]) => {
            const n = eventCounts[kind] ?? 0;
            return (
              <button
                key={kind}
                className={`btn small evt-toggle ${eventKinds[kind] !== false ? "active" : ""}`}
                // Carries its marker's colour, so the letter in the toolbar
                // and the disc on the chart teach each other. A tooltip only
                // helps someone who already suspects there is something to
                // hover, and "S" on its own tells you nothing.
                style={
                  // Three states, and the fill has to carry all of them. A
                  // neon chip with a grey letter reads as broken rather than
                  // as "there are none of these", so when the kind is absent
                  // the fill goes too -- only the hue survives, as an outline.
                  n === 0
                    ? { padding: "1px 5px", minWidth: 20 }
                    : eventKinds[kind] !== false
                      ? {
                          padding: "1px 5px", minWidth: 20,
                          background: EVENT_COLOR[kind],
                          borderColor: EVENT_COLOR[kind],
                          color: onColor(EVENT_COLOR[kind] ?? "#64748b"),
                        }
                      : { padding: "1px 5px", minWidth: 20, color: EVENT_COLOR[kind] }
                }
                disabled={n === 0}
                title={
                  n
                    ? `${label}: ${n} on this chart — click to hide`
                    : `No ${label.toLowerCase()} on this chart`
                }
                onClick={() =>
                  onEventKinds({ ...eventKinds, [kind]: eventKinds[kind] === false })
                }
              >
                {glyph}
              </button>
            );
          })}
        </span>
      </div>

      {intraday && (
        <button
          className={`btn small ${extendedHours ? "active" : ""}`}
          onClick={() => onExtendedHours(!extendedHours)}
          title={volumeNote ?? "Include pre- and post-market bars"}
        >
          ext hours
        </button>
      )}
      {volumeNote && (
        <span className="faint small volume-note" title={volumeNote}>
          {volumeNote.slice(0, 64)}…
        </span>
      )}

      {busy && <span className="spinner" />}

      {instances.map((inst) => {
        const def = byId.get(inst.indicator_id);
        const label = def?.short ?? inst.indicator_id;
        const shown = def
          ? def.params
              .map((p) => inst.params[p.name] ?? p.default)
              .filter((v) => typeof v === "number")
              .join(" ")
          : "";
        const hidden = inst.visible === false;
        return (
          <div
            key={inst.instance_id}
            className={`chip ${editing === inst.instance_id ? "editing" : ""} ${hidden ? "off" : ""}`}
            style={{ position: "relative" }}
          >
            <button
              className="chip-eye"
              title={hidden ? "Show" : "Hide"}
              onClick={() => onToggleVisible(inst.instance_id)}
            >
              <ToolIcon name={hidden ? "eye-off" : "eye"} size={11} />
            </button>
            <span
              className="swatch"
              style={{ background: def?.plots[0]?.color ?? "var(--accent)" }}
            />
            <span
              style={{ cursor: "pointer" }}
              onClick={() =>
                setEditing(editing === inst.instance_id ? null : inst.instance_id)
              }
              title={def?.name}
            >
              {label}{shown ? ` ${shown}` : ""}
            </span>
            <button onClick={() => onRemove(inst.instance_id)} title="Remove">×</button>

            {editing === inst.instance_id && def && (
              <div className="settings">
                <IndicatorSettings
                  def={def}
                  params={inst.params}
                  onChange={(patch) => onUpdate(inst.instance_id, patch)}
                />
              </div>
            )}
          </div>
        );
      })}

      {instances.length > 0 && (
        <button className="btn small" onClick={onClear} title="Remove all indicators">
          clear
        </button>
      )}
      {instances.length === 0 && (
        <span className="faint small">No indicators applied.</span>
      )}
    </div>
  );
}
